import React, { useEffect, useMemo, useCallback, useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
  useImageExperienceAdPreviewModel,
  AdPreviewCarouselLayout,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { PreviewPlacementContainer } from './preview-placement-container';
import { getPlacementTitle, normalizePreviewTexts } from './util';
import { getPreviewCarouselStyle } from './style';

// adPreviewConfig example:
// adPreviewConfig = {
//   adType: AdTypeEnum.MMA,
//   placementConfig: [
//       {
//         placementSetName: AdTypeEnum.MMA,
//         placementTypes: UAPPlacementTypeAlias.MMA.All,
//       },
//     ],
// }
const defaultPlaceHolderImage = {};
export const UnifiedAdPreviewCarouselLayoutWrapper = ({
  imageList,
  i18n,
  adPreviewConfig,
  previewTexts,
  previewLogo,
  enableAutoRotate,
  isMultimediaAd,
  isMultiMediaAdsPhase2Enabled,
}) => {
  const [index, setIndex] = useState(0);
  const { adType, placementConfig } = adPreviewConfig;
  const {
    firstImage,
    multiImagePlacementSets: placementSets,
    onUpdateImageExperienceImageList,
  } = useImageExperienceAdPreviewModel(placementConfig, defaultPlaceHolderImage);

  useEffect(() => {
    onUpdateImageExperienceImageList([imageList]);
  }, [imageList, onUpdateImageExperienceImageList]);

  const placementList = useMemo(() => {
    const list = _.chain(placementSets).map(placementSet => placementSet.placementList).flatten().value();
    return list;
  }, [placementSets]);

  const classes = useDefaultStyles(getPreviewCarouselStyle);

  const onRenderPlacement = useCallback((placement, AdPlacementComponent) => {
    const { placementMeta } = placement;
    const { imageMeta } = firstImage;

    if (!placementMeta || !imageMeta) {
      return null;
    }

    const unifiedPreviewTexts = normalizePreviewTexts(previewTexts, placementMeta.placementType);

    return (
      <PreviewPlacementContainer
        i18n={i18n}
        adType={adType}
        placementType={placementMeta.placementType}
        shouldShowAdditionalDisclaimer
      >
        <AdPlacementComponent
          isEnabled
          placementData={placement}
          {...unifiedPreviewTexts}
          logo={previewLogo}
          i18n={i18n}
          displayFinalUrlOrigin={isMultimediaAd && isMultiMediaAdsPhase2Enabled}
        />
      </PreviewPlacementContainer>
    );
  }, [adType, firstImage, i18n, isMultiMediaAdsPhase2Enabled, isMultimediaAd, previewLogo, previewTexts]);

  const title = useMemo(() => getPlacementTitle(adPreviewConfig, index, i18n), [adPreviewConfig, index, i18n]);

  const onActiveIndexChange = useCallback((newIndex) => {
    setIndex(newIndex);
  }, []);

  return (
    <div className={classes.unifiedCarouselLayoutWrapper}>
      {
        title &&
        <div className="preview-header">
          <div className={classnames('ad-preview-title', 'with-additional-tag')}>
            {title}
          </div>
          <div className="flex-grow" />
        </div>
      }
      <AdPreviewCarouselLayout
        placementList={placementList}
        onRenderPlacement={onRenderPlacement}
        i18n={i18n}
        enableAutoRotate={enableAutoRotate}
        onActiveIndexChange={onActiveIndexChange}
        customCarouselLayoutStyles={classes.customCarouselClassname}
        useCustomCarousel
      />
      <div className="disclaimer-info">
        <div className="disclaimer-info-inline">
          {i18n.getString('ImagePreview_Disclaimer_Message_MultimediaAd')}
        </div>
      </div>
    </div>
  );
};

UnifiedAdPreviewCarouselLayoutWrapper.propTypes = {
  imageList: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string,
  })).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  adPreviewConfig: PropTypes.shape({
    adType: PropTypes.string,
    placementConfig: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  previewTexts: PropTypes.shape({
    finalUrl: PropTypes.string,
    url: PropTypes.string,
  }),
  previewLogo: PropTypes.shape({}),
  showAdditionalDisclaimer: PropTypes.bool,
  enableAutoRotate: PropTypes.bool,
  isMultimediaAd: PropTypes.bool,
  isMultiMediaAdsPhase2Enabled: PropTypes.bool,
};

UnifiedAdPreviewCarouselLayoutWrapper.defaultProps = {
  adPreviewConfig: {},
  previewTexts: {},
  previewLogo: undefined,
  showAdditionalDisclaimer: false,
  enableAutoRotate: false,
  isMultimediaAd: false,
  isMultiMediaAdsPhase2Enabled: false,
};
